import { Table } from "@weirgroup/weir-dls";
import { Dispatch, SetStateAction, useEffect, useState, useRef, forwardRef, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";
import { dashboardTableHeaders, keysToDelete } from "../../../constants/bucket.contants";
import { BucketDataType } from "../../../types/bucketTypes";
import { getTableHeaders, groupData } from "../../../utils/table.util";
import styled from "styled-components";
import "../styles.scss";
import { useDarkMode } from "../../../hooks/use-darkmode";
import { useNavigate } from "react-router-dom";
import { routePath } from "../../../constants/navigation.constants";
import { useDispatch } from "react-redux";
import { setSelectedAssets, setTagInfo } from "../../../redux/actions";

// Define the props interface for the TableView component
interface Props {
  bucketList: BucketDataType[];
  setSelectedAsset: Dispatch<SetStateAction<BucketDataType>>;
  isFetchingGeofences: boolean;
  getTagData: any;
  setTagData: Dispatch<any>;
}

export type TableViewRef = {
  resetTablePage: () => void;
};

// Define dropdown options for rows per page
let dropdownOptions =[
  {
    "name": "5",
    "value": 5
  },
  {
    "name": "10",
    "value": 10
  },
  {
    "name": "15",
    "value": 15
  },
  {
    "name": "25",
    "value": 25
  },
  {
    "name": "50",
    "value": 50
  },
]

// Styled component for the table wrapper
const TableWrapper = styled.div<{ $containerWidth?: number }>`
  flex: 1;
  width: 100%;
  font-family: Roboto, sans-serif;
  & > div,
  & [data-testid="dls-table-headerTitleWrapper"] tbody tr > .dls-grid-container,
  & > div > div {
    width: ${({ $containerWidth }) => $containerWidth}px;
  }
`;

// TableView component definition
export const TableView = forwardRef<TableViewRef, Props>((props: Props, ref) => {
  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement>(null); // Reference to the container div
  const [containerWidth, setContainerWidth] = useState(0); // State for container width
  const { bucketList, setSelectedAsset, isFetchingGeofences, getTagData, setTagData } = props;
  const [headerData, setHeaderData] = useState<any>([]); // State for table headers
  const [sourceData, setSourceData] = useState<any>([]); // State for table data
  const [rowCount, setRowCount] = useState<number>(15); // State for rows per page
  const [pagedData, setPagedData] = useState<BucketDataType[]>([]); // State for paged data
  const [currentPage, setCurrentPage] = useState<number>(1); // State for current page
  const {darkMode} = useDarkMode()
  const { t } = useTranslation(); // Translation hook
  const navigate = useNavigate();

  useImperativeHandle(ref, () => ({
    resetTablePage: () => {
      setCurrentPage(1);
    }
  }));

  // Function to delete specified keys from bucket data
  function DeleteKeys(bucketData: BucketDataType[], keysToDelete: any) {
    bucketData?.forEach((bucket: any) => {
      for (let index = 0; index < keysToDelete.length; index++) {
        delete bucket[keysToDelete[index]];
      }
    });
    return bucketData;
  }
  
  const saveStateToLocalStorage = (key: string, state: any): void => {
    try {
        const serializedState = JSON.stringify(state);
        localStorage.setItem(key, serializedState);
      } catch (err) {
        console.error("Could not save state", err);
    }
  };

  // Handle row click in the table
  function handleRowClick(e: any) {
    try {
      const objKeys = Object.keys(e);
      const objValues: any = Object.values(e);
      let selectedAsset: any = {};

      objKeys.forEach((objkey: any, i: any) => {
        selectedAsset[objkey] = objValues[i].value;
      });
      // Only proceed if asset_id is valid
      if (selectedAsset.asset_id !== undefined && selectedAsset.asset_id !== null) {
        // Fetch tag data for the selected asset
        let tagInfo = getTagData(selectedAsset);
        setTagData(tagInfo);
        setSelectedAsset(selectedAsset);
        dispatch(setSelectedAssets(selectedAsset));
        dispatch(setTagInfo(tagInfo));
        navigate(routePath.ASSET_DETAIL);
        saveStateToLocalStorage('selectedAsset', selectedAsset);
        saveStateToLocalStorage('tagInfo', tagInfo);
      } else {
        console.warn('Row click ignored: asset_id is undefined or null', selectedAsset);
        // Optionally, show a user-friendly message here
        return;
      }
    } catch (error) {
      console.error(error);
    }
  }

  // Fetch and process data for the table
  function fetchData() {
    const updatedData = DeleteKeys(bucketList, keysToDelete);
    const tableHeaders = getTableHeaders(dashboardTableHeaders);
    setHeaderData(tableHeaders);
    setSourceData(updatedData);
  }

  // Effect to observe container width changes
  useEffect(() => {
    const observer = new ResizeObserver(entries => {
      setContainerWidth(entries[0].contentRect.width);
    });
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  // Effect to fetch data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  // Effect to fetch data when bucketList changes
  useEffect(() => {
    fetchData();
  }, [bucketList]);

  // Effect to update paged data when sourceData or rowCount changes
  useEffect(() => {
    setPagedData(groupData(sourceData, rowCount));
  }, [sourceData, rowCount]);

  return (
    <div className={`table-view-container ${isFetchingGeofences ? "disable-pointer" : ""}`}>
      {bucketList.length == 0 ? (
        <div className="noResults">
          <p>{t("noResultsFound").toString()}</p>
        </div>
      ) : (
        <div className="table-view" ref={containerRef}>
          {(pagedData?.length > 0) && (headerData?.length > 0) ? (
            <TableWrapper $containerWidth={containerWidth}>
              <Table
                darkMode={darkMode}
                header={headerData}
                source={pagedData.length === 1 ?pagedData[0] : pagedData[currentPage - 1]}
                title={t("yourAssets")}
                rowClickable={true}
                onRowClick={(e: any) => handleRowClick(e)}
                noResultsText={t("noResultsFound")}
                pagination
                rowsPerPageDropdownOptions={dropdownOptions}
                onRowsPerPageDropdownChange={(page: number) => {
                  setCurrentPage(1);
                  setRowCount(page);
                }}
                onPageChange={(page: number) => {
                  setCurrentPage(page);
                }}
                rowsPerPageDropdownValue={rowCount}
                currentPage={currentPage}
                maxPages={pagedData?.length}
                primaryActionBtnStatus={false}
                key={currentPage} // Key to reset internal state on page change
              />
            </TableWrapper>
          ) : (
            <></>
          )}
        </div>
      )}
    </div>
  );
})